import axios from "axios";
import { isBackendDownError, notifyBackendUnreachable } from "@/lib/backendStatus";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

// Portal (student) traffic uses a separate token in localStorage so the
// admin session on the same machine isn't clobbered. Any request whose path
// targets a portal-scoped endpoint gets the portal token; everything else
// keeps the admin token.
function isPortalRequest(url) {
  if (!url) return false;
  return url.startsWith("/student/") || url.startsWith("/auth/student/");
}

function activeTokenFor(url) {
  return isPortalRequest(url)
    ? localStorage.getItem("portal_token")
    : localStorage.getItem("token");
}

API.interceptors.request.use((config) => {
  const token = activeTokenFor(config.url);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Session ID tracking — separate keys for portal/admin so the two flows
  // don't cross-count in the ActiveSessions table.
  const sessionKey = isPortalRequest(config.url) ? "portal_session_id" : "session_id";
  const sessionId = localStorage.getItem(sessionKey);
  if (sessionId) {
    config.headers["X-Session-Id"] = sessionId;
  }

  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (isBackendDownError(err)) {
      notifyBackendUnreachable();
    }
    if (err.response?.status === 401) {
      // Decide which session died from the REQUEST that failed, not from the
      // page we happen to be on. Both auth contexts validate their tokens at
      // app mount on every route, so a stale portal token 401-ing while an
      // admin browses /dashboard must never log the admin out (and vice
      // versa). Redirect only when the current page belongs to the session
      // that just expired; otherwise clear the dead token silently.
      const portalRequest = isPortalRequest(err.config?.url);
      const path = window.location.pathname;
      // NOTE: /portal-theme is an ADMIN page — match /portal exactly or /portal/*.
      const isPortalPage = path === "/portal" || path.startsWith("/portal/");
      // Public taker pages (/quiz/:slug, /s/:slug) and login pages must never
      // hard-redirect; everything else outside the portal is the admin app.
      const isPublicOrLoginPage =
        path.startsWith("/quiz/") || path.startsWith("/s/") ||
        path === "/login" || path.endsWith("/login") || path === "/";
      const isAdminPage = !isPortalPage && !isPublicOrLoginPage;

      if (portalRequest) {
        localStorage.removeItem("portal_token");
        localStorage.removeItem("portal_user");
        localStorage.removeItem("portal_organization");
        localStorage.removeItem("portal_session_id");
        if (isPortalPage) {
          // Carry the current location so re-login can land back on the same
          // tab instead of resetting to Overview.
          const next = encodeURIComponent(
            window.location.pathname + window.location.search
          );
          window.location.href = `/portal/login?expired=1&next=${next}`;
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (isAdminPage) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

// The backend's global exception handler wraps every error as
// { error: { code, message, details } } (see middleware/exception_handler.py).
// Older code expected FastAPI's raw { detail } shape — accept both.
// Returns { status, message, details } where details is the structured dict
// the route attached (e.g. { requires_force, open_attempts }) or null.
export function extractApiError(err, fallbackMessage = "Request failed") {
  const status = err?.response?.status ?? null;
  const data = err?.response?.data;
  const envelope = data?.error;
  const rawDetail = data?.detail;

  const details =
    (envelope?.details && typeof envelope.details === "object" ? envelope.details : null) ||
    (rawDetail && typeof rawDetail === "object" ? rawDetail : null);

  const message =
    envelope?.message ||
    details?.message ||
    (typeof rawDetail === "string" ? rawDetail : null) ||
    (typeof data?.message === "string" ? data.message : null) ||
    err?.message ||
    fallbackMessage;

  return { status, message, details };
}

export default API;
